import {
    Component,
    inject,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import {
    ButtonModule,
    ButtonStyle,
    CardModule,
    DirectivesModule,
} from 'rcp-common-lib';
import { MatProgressBar } from '@angular/material/progress-bar';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
    AgingProgressReportRequest,
    ClaimQueryControllerService,
} from '@core/generated/openapi';
import dayjs from 'dayjs';
import { NavigationBehaviorOptions, Router } from '@angular/router';
import { AuthService } from '@services/auth/auth.service';
import { isNil } from 'lodash-es';
import {
    AgingRoutes,
    ModuleRoutes,
} from '@core/constants/module-history.constants';
import { AgingInsightsMetricsService } from '@services/insights-metrics/aging-insights-metrics/aging-insights-metrics.service';
import { Metrics } from '@core/constants/insights-metrics.constants';
import { FormGroup } from '@angular/forms';
import { ClaimStalledFilterService } from '@services/claim-stalled-filter/claim-stalled-filter.service';
import { STALLED_CLAIM_FILTER_IDS } from '@core/constants/stalled-claim-filter.constants';
import { skip } from 'rxjs';

@UntilDestroy()
@Component({
    selector: 'rcp-oup-claim-progress-metric',
    imports: [CardModule, DirectivesModule, MatProgressBar, ButtonModule],
    templateUrl: './claim-progress-metric.component.html',
    styleUrl: './claim-progress-metric.component.scss',
})
export class ClaimProgressMetricComponent implements OnInit, OnChanges {
    private readonly claimStalledFilterService = inject(
        ClaimStalledFilterService,
    );

    @Input() isInsightsDetailView: boolean = false;
    @Input() detailSearchCriteria: FormGroup;
    @Input() claimStalledFilterId: string;

    workedClaims: number;
    totalClaims: number;
    workedClaimPercent: number;
    isLoading: boolean = false;
    locations: number[];

    isChartInitialized: boolean = false;

    constructor(
        private router: Router,
        private claimQueryControllerService: ClaimQueryControllerService,
        private authService: AuthService,
        private insightsMetricsService: AgingInsightsMetricsService,
    ) {}

    ngOnInit(): void {
        this.getSelectedLocations();
        this.onStalledFilterChange();
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('detailSearchCriteria' in changes) {
            if (this.isChartInitialized) {
                this.loadData();
            }
        }
    }

    getSelectedLocations() {
        this.authService.agingLocationFilter
            .pipe(untilDestroyed(this))
            .subscribe((locations) => {
                if (this.authService.isAgingUploadByLocationOff(locations)) {
                    this.locations = [];
                    this.loadData();
                } else if (!isNil(locations)) {
                    this.locations = locations;
                    this.loadData();
                }
            });
    }

    onStalledFilterChange() {
        if (this.claimStalledFilterId) {
            this.claimStalledFilterService
                .onFilterStatusChange(this.claimStalledFilterId)
                .pipe(untilDestroyed(this), skip(1))
                .subscribe({
                    next: () => this.loadData(),
                });
        }
    }

    loadData() {
        this.isLoading = true;

        let request: AgingProgressReportRequest = this.isInsightsDetailView
            ? this.getDetailReportRequest()
            : this.getDefaultReportRequest();

        this.claimQueryControllerService
            .getClaimProgressMetric(request)
            .pipe(untilDestroyed(this))
            .subscribe((metrics) => {
                this.workedClaims = metrics.workedClaims;
                this.totalClaims = metrics.totalClaims;
                this.workedClaimPercent = Math.round(
                    metrics.workedClaimPercent,
                );
                this.isChartInitialized = true;
                this.isLoading = false;
            });
    }

    getDefaultReportRequest(): AgingProgressReportRequest {
        return <AgingProgressReportRequest>{
            monthYear: {
                year: dayjs().year(),
                month: dayjs().month() + 1,
            },
            accountLocationIds: this.locations,
            payerType: AgingProgressReportRequest.PayerTypeEnum.AllPayers,
            claimStatus: AgingProgressReportRequest.ClaimStatusEnum.AllStatuses,
            negativeBalance:
                AgingProgressReportRequest.NegativeBalanceEnum
                    .IncludeNegativeBalances,
            nonStalledClaimsOnly:
                !this.claimStalledFilterService.getFilterStatusValue(
                    this.claimStalledFilterId,
                ),
        };
    }

    getDetailReportRequest(): AgingProgressReportRequest {
        const [selectedMonth, selectedYear] =
            this.detailSearchCriteria?.controls['monthYear'].value.split('_');

        return <AgingProgressReportRequest>{
            monthYear: {
                year: selectedYear,
                month: selectedMonth,
            },
            accountLocationIds: this.locations,
            payerType:
                this.detailSearchCriteria?.controls['payerType'].getRawValue(),
            claimStatus:
                this.detailSearchCriteria?.controls[
                    'claimStatus'
                ].getRawValue(),
            negativeBalance:
                this.detailSearchCriteria?.controls[
                    'negativeBalance'
                ].getRawValue(),
            nonStalledClaimsOnly:
                !this.detailSearchCriteria?.controls[
                    'includeStalled'
                ].getRawValue(),
        };
    }

    routeToReport() {
        this.insightsMetricsService.selectedAgingMetric.next(
            Metrics.AgingProgress,
        );

        // Update detail filter to match current filter
        this.claimStalledFilterService.updateFilterStatus(
            STALLED_CLAIM_FILTER_IDS.INSIGHTS_AGING_PROGRESS_DETAIL,
            this.claimStalledFilterService.getFilterStatusValue(
                this.claimStalledFilterId,
            ),
        );

        this.router.navigateByUrl(
            ModuleRoutes.Aging + '/' + AgingRoutes.Insights + '/detail',
            <NavigationBehaviorOptions>{
                state: {
                    fromViewDetails: true,
                },
            },
        );
    }

    protected readonly ButtonStyle = ButtonStyle;
}
